import { Skeleton } from '../../components/ui/Skeleton'
import type { PipelineBucket } from './domain'
import { formatMoney } from './domain'

export interface StatCardProps {
  readonly bucket: PipelineBucket
  readonly label: string
  readonly count: number
  readonly units: number
  readonly amount: number
  readonly currency: string
  readonly active: boolean
  readonly onSelect: (bucket: PipelineBucket) => void
}

/**
 * Pipeline stat tile (06-orders.png "Order pipeline" card, one of 4 tiles
 * inside it; UX spec §1.2 item 2 / components.md §5 net-new composite).
 *
 * The handoff's tile has NO separate status icon — only a small color
 * swatch dot next to the text label (dot is `aria-hidden`; the label text
 * is the real, non-color-dependent status signal, WCAG 2.2 AA "color is
 * never the sole conveyor of state"); the count renders on its own line
 * below, with no dot of its own (UID-399 — corrects UID-398, a design
 * mistake that had moved the dot down next to the count instead). The
 * earlier build additionally
 * rendered a redundant `StatusIcon` here; removed to match the mock exactly
 * — the label text alone still satisfies the color-independence requirement.
 *
 * A real `<button>` — not a decorative div — so it clears the 24x24px
 * minimum target size even though the whole tile is much larger, and
 * doubles as a quick filter (§1.8): clicking activates the matching toolbar
 * filter chip. `aria-pressed` reflects whether this bucket's filter is
 * currently active; the active visual (tinted background + accent border)
 * mirrors the handoff's own `tileStyle`/`dotStyle` per-bucket accent colors.
 */
export function StatCard({
  bucket,
  label,
  count,
  units,
  amount,
  currency,
  active,
  onSelect,
}: StatCardProps) {
  return (
    <button
      type="button"
      className={`stat-card-tile stat-card-tile-${bucket}${active ? ' stat-card-tile-active' : ''}`}
      aria-pressed={active}
      onClick={() => onSelect(bucket)}
    >
      <span className="stat-card-header">
        <span className={`stat-card-dot stat-card-dot-${bucket}`} aria-hidden="true" />
        <span className="stat-card-label">{label}</span>
      </span>
      <span className="stat-card-primary">{count}</span>
      <span className="stat-card-secondary">
        {units.toLocaleString()} units ·{' '}
        {/* UID-123 item 10: an empty/zero-value bucket renders "—", matching
        the design's `val ? money(val) : '—'` pattern (`Fictiv Platform.dc.html:8083`)
        — a bucket with $0.00 reads as "no money here yet," not a real total. */}
        {amount ? formatMoney(amount, currency) : '—'}
      </span>
    </button>
  )
}

/** Shimmer placeholder matching the real tile's shape (UX spec §1.4 Loading). */
export function StatCardSkeleton() {
  return (
    <div className="stat-card-tile" aria-hidden="true">
      <Skeleton width="60%" height={14} />
      <Skeleton width="40%" height={28} className="stat-card-skeleton-primary" />
      <Skeleton width="80%" height={12} />
    </div>
  )
}
